import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Star, Trophy, TrendingUp, Loader2, HelpCircle, ChevronDown, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo, { faqJsonLd } from "@/components/Seo";
import Linkify from "@/components/Linkify";

const SPONSOR_FAQ = [
  // --- Pricing & Packages ---
  { q: "What do I get as a sponsor?",
    a: "A verified business listing in the FanFest directory, a printable QR check-in code, real-time analytics on scans and coupon redemptions, marketing-consent leads (name + email of every fan who opts in), a Watch Party Partner™ badge (Gold+), inclusion in campaign emails/push broadcasts, and depending on tier — homepage placement and livestream logo exposure." },
  { q: "How much does sponsorship cost?",
    a: "Four one-time packages: Bronze $250, Silver $1,000, Gold $2,500, Title $5,000. Payment is one flat fee for the full campaign — no monthly billing, no auto-renewals." },
  { q: "How long does the sponsorship last?",
    a: "One full campaign — from July 6 through July 19, 2026 (World Cup Final day). Renewals for future FanFest campaigns (Jaguars season, Restaurant Week, holidays, Fourth of July, concerts) are sold separately." },
  { q: "Can I upgrade my tier later?",
    a: "Yes. Purchase the higher tier and email support@myadnlocal.com within 24 hours — we'll credit the amount you already paid toward the upgrade." },
  { q: "Do you offer refunds?",
    a: "Full refund within 7 days of purchase as long as no live campaign traffic has been driven to your listing. After that, refunds are pro-rated at the ADN team's discretion. Email support@myadnlocal.com to request." },
  { q: "How do I get a receipt or invoice for my accountant?",
    a: "Stripe automatically emails a paid receipt to the email on your business account. Need a formal invoice with your EIN? Reply to that receipt and we'll send one within one business day." },
  { q: "Are there taxes on the sponsorship fee?",
    a: "Florida sales tax is included where applicable and shown on your Stripe receipt. Out-of-state buyers are not charged Florida sales tax." },

  // --- Getting Started ---
  { q: "How fast does my listing go live after I pay?",
    a: "Instantly. Stripe confirms the payment, your tier activates on your business profile within 5 seconds, and the featured badge is applied to your directory listing on the next fan page load." },
  { q: "What do I need to do to set up my profile?",
    a: "Register a free business account, fill in your business name, address (used for map placement + GPS check-in verification), a short description, a photo, and hours. That's it — the QR code is generated automatically and you can print it right from the QR Center page." },
  { q: "How long does full setup take?",
    a: "About 10 minutes — 5 to fill in the profile, 3 to add your first 2–3 offers (coupons/deals), and 2 to print the QR flyer and post it at your venue." },
  { q: "Can more than one person from my business manage the account?",
    a: "Right now it's one login per business. Multi-seat team access is on the roadmap — email support@myadnlocal.com if you need it and we'll flag your account for early access." },

  // --- Offers, Leads & Analytics ---
  { q: "How do I create offers or coupons?",
    a: "Go to Business Dashboard → Offers → New Offer. Set a title (e.g., '$5 off any pitcher'), description, and expiration. Redemption is tracked per fan when they scan the offer QR at your venue." },
  { q: "What analytics do I get?",
    a: "Profile views, unique visitors, check-in scans, offer redemptions, marketing opt-in count, 30-day trend chart, estimated revenue (based on your average ticket size), rank among partners, and sponsor tier. All refreshed in real time from Business Dashboard → Analytics." },
  { q: "How do I get the marketing leads (names / emails)?",
    a: "Every fan who checks in and opts into marketing consent is added to your Leads page (Business → Leads) with name, email, and check-in date. Export as CSV any time. Leads are yours to keep after the campaign ends — no data lock-in." },
  { q: "What's the average ROI a sponsor sees?",
    a: "Depends on the tier and your offers. Historical benchmark: Gold sponsors typically see 40–120 unique check-ins and 15–35 redemptions per campaign. Your Business → Analytics page shows YOUR live numbers, not projections." },

  // --- QR & Check-ins ---
  { q: "How does the QR check-in actually work?",
    a: "You post the QR code (printable flyer from your dashboard) at your venue. Fans open the FanFest app, tap Check In, scan the code, and (if within 0.5 miles GPS-verified) get a Fan Passport stamp. Each unique daily check-in also enters them into the sweepstakes." },
  { q: "Do I need a special device or app to accept check-ins?",
    a: "No hardware. All you need is the printed QR flyer on a table or window. The fan's phone does everything." },
  { q: "Can I print my own custom QR flyer?",
    a: "Yes — download the branded 8.5×11 PDF from Business → QR Center → Download Flyer. Print at any office/print shop." },

  // --- Streak Boost Multiplier ---
  { q: "What is the Streak Boost Multiplier and how does it drive traffic?",
    a: "You buy a single-day multiplier (2X to 5X) that turns every check-in at your venue that day into 2X–5X toward the fan's 7-day passport streak. Fans chasing streak milestones will specifically choose your venue that day — it's the single best driver of same-day foot traffic on the platform." },
  { q: "How much does a Streak Boost cost?",
    a: "Prices scale with multiplier: 2X ~$49, 3X ~$99, 4X ~$149, 5X ~$199 per day. Purchased separately from your tier via Business → Streak Boost Shop." },

  // --- Legal & Compliance ---
  { q: "Is my sponsorship considered sweepstakes prize consideration?",
    a: "No. Sponsors do not fund prizes and check-ins are free for fans. The sweepstakes is No Purchase Necessary under Florida law. Full rules at /rules." },
  { q: "Do I need to offer a discount or free item to fans?",
    a: "Not required. Offers are optional and only exist to help you convert check-ins into paying customers. Some sponsors run zero offers and just use the platform for discovery + leads." },
  { q: "Is there any exclusivity between sponsors?",
    a: "Only the Title tier ($5,000) is exclusive — one Title sponsor per campaign. Gold and below have no category exclusivity, so multiple bars, restaurants, or venues in the same category can co-sponsor." },

  // --- Support ---
  { q: "How do I contact the ADN Local team?",
    a: "Email support@myadnlocal.com for sales/billing questions, or support@myadnlocal.com for technical help. We aim to reply within one business day, faster during active campaign weeks." },
  { q: "Can I speak with someone live before buying?",
    a: "Yes — email support@myadnlocal.com with your business name, city, and best times, and we'll set up a 15-minute call to walk through the platform." },
];

const TIERS = [
  { id: "bronze",   name: "Bronze",   price: 250,  color: "text-amber-500 border-amber-500/60",
    perks: ["Verified listing", "5 offers", "Basic analytics"] },
  { id: "silver",   name: "Silver",   price: 1000, color: "text-slate-300 border-slate-400/60",
    perks: ["Featured tag", "Up to 10 offers", "Standard analytics", "Directory badge"] },
  { id: "gold",     name: "Gold",     price: 2500, color: "text-primary border-primary",
    perks: ["Featured directory placement", "Watch Party Partner™ badge", "Up to 20 offers", "Full analytics", "Social promotion"], highlight: true },
  { id: "title",    name: "Title",    price: 5000, color: "text-secondary border-secondary",
    perks: ["Homepage placement", "Named title sponsor", "Livestream logo", "Unlimited offers", "Full analytics", "Priority listing"] },
];

export default function SponsorInfo() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");

  const purchase = async (packageId) => {
    if (!user) {
      toast.info("Sign in as a business to purchase a tier.");
      nav("/register?role=business");
      return;
    }
    if (user.role === "consumer") {
      toast.error("Sponsor tiers are for business accounts.");
      return;
    }
    setBusy(packageId);
    try {
      const { data } = await api.post("/sponsor/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Set up your business profile first.");
      setBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <Seo
        title="Become a Sponsor — Jacksonville Watch Party Partners"
        description="Sponsor tiers for local Jacksonville businesses: Bronze $250, Silver $1,000, Gold $2,500, Title $5,000. Get discovered, drive check-ins, and track ROI in real time during ADN Local FanFest 2026."
        jsonLd={faqJsonLd(SPONSOR_FAQ)}
      />
      <div className="text-xs tracking-[0.3em] uppercase text-primary">Businesses & Sponsors</div>
          <h1 className="font-bebas text-5xl mt-2">POWER JACKSONVILLE&apos;S BIGGEST WATCH PARTY</h1>
      <p className="text-white/70 mt-3 max-w-2xl">Get discovered, drive check-ins, and turn every match-day into new customers. Purchase your tier instantly — activation is immediate.</p>

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {[
          { icon: Star, title: "GET DISCOVERED", body: "Featured in directory, homepage carousel, and event maps." },
          { icon: Trophy, title: "DRIVE CHECK-INS", body: "Each fan scan = new customer + Fan Passport™ stamp on your card." },
          { icon: TrendingUp, title: "TRACK RESULTS", body: "Real-time views, scans, redemptions, and marketing consent leads." },
        ].map((b,i)=>(
          <div key={i} className="adn-card p-6">
            <b.icon className="text-primary"/>
            <h3 className="font-bebas text-2xl mt-2">{b.title}</h3>
            <p className="text-white/70 text-sm mt-1">{b.body}</p>
          </div>
        ))}
      </div>

      <h2 className="font-bebas text-4xl mt-16">CHOOSE YOUR TIER</h2>
      <p className="text-white/60 text-sm mt-1">Secure checkout via Stripe. Instant activation.</p>
      <div className="grid md:grid-cols-4 gap-4 mt-6">
        {TIERS.map(t => (
          <div key={t.id}
            data-testid={`tier-card-${t.id}`}
            className={`adn-card p-6 border-t-4 ${t.color} flex flex-col ${t.highlight ? "ring-2 ring-primary/30" : ""}`}>
            {t.highlight && (
              <div className="text-[10px] tracking-widest uppercase text-primary font-semibold mb-1">Most popular</div>
            )}
            <div className={`font-bebas text-3xl ${t.color.split(" ")[0]}`}>{t.name.toUpperCase()}</div>
            <div className="text-3xl font-bold mt-1">${t.price.toLocaleString()}</div>
            <div className="text-xs text-white/50 mb-3">one-time · full campaign</div>
            <ul className="space-y-2 text-sm text-white/80 flex-1">
              {t.perks.map(p => <li key={p} className="flex gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0"/>{p}</li>)}
            </ul>
            <button
              onClick={() => purchase(t.id)}
              disabled={!!busy}
              data-testid={`tier-buy-${t.id}`}
              className="mt-5 w-full py-3 bg-primary text-black font-bebas text-xl rounded-sm hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-2">
              {busy === t.id ? (<><Loader2 size={16} className="animate-spin"/> REDIRECTING…</>) : `SELECT ${t.name.toUpperCase()}`}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-white/50 max-w-3xl">
        Payments are processed by Stripe in test mode. Sponsor tier activates on your business profile immediately after payment. For a custom package or dedicated support, email <a href="mailto:support@myadnlocal.com" className="text-primary underline">support@myadnlocal.com</a>.
      </div>

      <SponsorFaqSection faqs={SPONSOR_FAQ} />

      <div className="mt-14 adn-card p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-1">
          <h3 className="font-bebas text-3xl">NOT A SPONSOR YET?</h3>
          <p className="text-white/70 mt-1">Create your free business account, complete your profile, then upgrade to any tier above.</p>
        </div>
        <Link to="/register?role=business" data-testid="sponsor-signup-cta"
          className="px-6 py-4 bg-secondary text-black font-bebas text-xl rounded-sm hover:brightness-110">
          CREATE BUSINESS ACCOUNT
        </Link>
      </div>
    </div>
  );
}

function SponsorFaqSection({ faqs }) {
  const [query, setQuery] = useState("");
  const [openIdx, setOpenIdx] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [faqs, query]);

  return (
    <section className="mt-16" id="sponsor-faq" data-testid="sponsor-faq">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-primary">
            <HelpCircle size={14} /> Sponsor Q&amp;A
          </div>
          <h2 className="font-bebas text-4xl mt-2">EVERYTHING SPONSORS ASK — ANSWERED</h2>
          <p className="text-white/60 text-sm mt-1 max-w-2xl">
            Pricing, refunds, analytics, leads, taxes, QR setup, Streak Boosts, exclusivity, compliance — search or scroll. Still stuck? Email <a href="mailto:support@myadnlocal.com" className="text-primary underline">support@myadnlocal.com</a>.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#0b1428] border border-adn-border rounded px-3 py-2 min-w-[260px] w-full sm:w-auto">
          <Search size={14} className="text-white/50 shrink-0"/>
          <input
            data-testid="sponsor-faq-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sponsor questions…"
            className="bg-transparent outline-none text-sm flex-1 placeholder:text-white/40"
          />
        </div>
      </div>

      <div className="mt-6 adn-card divide-y divide-adn-border">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-white/50 text-sm">
            No sponsor question matches your search. Try different keywords or email support@myadnlocal.com.
          </div>
        )}
        {filtered.map((f, i) => {
          const isOpen = openIdx === i;
          return (
            <button
              key={f.q}
              type="button"
              onClick={() => setOpenIdx(isOpen ? null : i)}
              data-testid={`sponsor-faq-item-${i}`}
              className="w-full text-left p-5 hover:bg-white/[0.02] transition-colors"
              aria-expanded={isOpen}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="font-semibold text-white pr-2">{f.q}</div>
                <ChevronDown
                  size={18}
                  className={`text-primary shrink-0 mt-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
              {isOpen && (
                <div className="mt-3 text-sm text-white/75 leading-relaxed" data-testid={`sponsor-faq-answer-${i}`}>
                  <Linkify>{f.a}</Linkify>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
